import {
  Box, VStack, Heading, Text, Container, SimpleGrid,
  FormControl, FormLabel, Input, Textarea, Button, useColorModeValue,
  useToast, Link, HStack, Icon
} from '@chakra-ui/react';
import { useState } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';

const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? process.env.REACT_APP_API_BASE_URL_DEV
  : process.env.REACT_APP_API_BASE_URL_PROD;

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/landingAuth/send-contact-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'Message sent!',
          description: 'Your message has been sent successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        setFormData({ name: '', email: '', phone: '', message: '' }); // reset form
      } else {
        toast({
          title: 'Error',
          description: data.message || 'Something went wrong. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (err) {
      console.error('Error sending message:', err);
      toast({
        title: 'Error',
        description: 'An error occurred. Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setLoading(false);
    }
  };

  const accentColor = useColorModeValue('yellow.400', 'yellow.500');
  const cardBg = useColorModeValue('white', 'gray.700');

  return (
    <Box minH="100vh" position="relative" overflow="hidden" py={{ base: 16, md: 24 }} marginBottom="24">
      {/* Decorative Elements */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position="absolute"
        top="10%"
        left="5%"
        w="120px"
        h="120px"
        bg="yellow.200"
        borderRadius="50% 40% 60% 30%"
        opacity="0.6"
        zIndex="0"
        animation="float 6s ease-in-out infinite"
        sx={{
          '@keyframes float': {
            '0%,100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-20px)' }
          }
        }}
      />
      <Box
        display={{ base: 'none', lg: 'block' }}
        position="absolute"
        bottom="15%"
        right="10%"
        w="150px"
        h="150px"
        bg="orange.300"
        borderRadius="40% 60% 30% 70%"
        opacity="0.5"
        zIndex="0"
        animation="pulse 5s ease-in-out infinite"
        sx={{
          '@keyframes pulse': {
            '0%,100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.1)' }
          }
        }}
      />
      <Container maxW="7xl" position="relative" zIndex="1">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 12, lg: 20 }} alignItems="center">
          {/* Contact Info */}
          <VStack spacing={8} align={{ base: 'center', lg: 'start' }} textAlign={{ base: 'center', lg: 'left' }} w="full" marginLeft={{ md: "10" }}>
            <VStack spacing={4} align={{ base: 'center', lg: 'start' }} w="full">
              <Heading as="h2" size="2xl" color="gray.800" fontWeight="bold" position="relative">
                Get in Touch
                <Box
                  position="absolute"
                  bottom={-2}
                  left="0"
                  w="100%"
                  h="3px"
                  bg={accentColor}
                  borderRadius="full"
                  transform="rotate(-2deg)"
                  opacity={0.8}
                />
              </Heading>
              <Text fontSize="lg" color="gray.700" maxW="md">
                Ready to transform your restaurant? Have some questions, comments or compliments? Let's start the conversation.
              </Text>
            </VStack>
            <VStack spacing={5} align={{ base: 'center', lg: 'start' }} w="full">
              <Box textAlign={{ base: 'center', lg: 'left' }}>
                <Text fontWeight="bold" color="gray.800">Email</Text>
                <Text color="gray.900" fontSize="xl">sankalp@piepos.in</Text>
              </Box>

              {/* Company Presence Section */}
              <Box textAlign={{ base: 'center', lg: 'left' }}>
                <Text fontWeight="bold" color="gray.800" mb={2}>Company</Text>
                <Link
                  href="https://wellfound.com/company/piepos-in/"
                  isExternal
                  display="inline-flex"
                  alignItems="center"
                  color="blue.600"
                  fontSize="lg"
                  fontWeight="medium"
                  _hover={{
                    color: "blue.800",
                    textDecoration: "none",
                    transform: "translateY(-1px)"
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={2}>
                    <Text>PiePos on AngelList</Text>
                    <Icon as={FaExternalLinkAlt} boxSize={4} />
                  </HStack>
                </Link>
                <Text color="gray.600" fontSize="sm" mt={1}>
                  Learn more about our company and team
                </Text>
              </Box>

              {/* Buy me a Coffee */}
              <Box textAlign={{ base: 'center', lg: 'left' }}>
                <Text fontWeight="bold" color="gray.800" mb={2}>Buy Me A Coffee</Text>
                <Link
                  href="https://buymeacoffee.com/piepos"
                  isExternal
                  display="inline-flex"
                  alignItems="center"
                  color="blue.600"
                  fontSize="lg"
                  fontWeight="medium"
                  _hover={{
                    color: "blue.800",
                    textDecoration: "none",
                    transform: "translateY(-1px)"
                  }}
                  transition="all 0.2s"
                >
                  <HStack spacing={2}>
                    <Text>Buy Me A Coffee ❤️</Text>
                    <Icon as={FaExternalLinkAlt} boxSize={4} />
                  </HStack>
                </Link>
                <Text color="gray.600" fontSize="sm" mt={1}>
                  Show Support for PiePOS
                </Text>
              </Box>
            </VStack>
          </VStack>
          {/* Contact Form */}
          <Box
            bg={cardBg}
            p={{ base: 6, md: 10 }}
            borderRadius="2xl"
            boxShadow="2xl"
            border="1px solid"
            borderColor="gray.200"
          >
            <form onSubmit={handleSubmit}>
              <VStack spacing={6}>
                <Heading as="h3" size="lg" color="gray.800" textAlign="center">
                  Send us a message
                </Heading>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" color="gray.700">Name</FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    borderRadius="xl"
                    borderColor="gray.300"
                    focusBorderColor={accentColor}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" color="gray.700">Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    borderRadius="xl"
                    borderColor="gray.300"
                    focusBorderColor={accentColor}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.700">Phone</FormLabel>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your phone number"
                    borderRadius="xl"
                    borderColor="gray.300"
                    focusBorderColor={accentColor}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" color="gray.700">Message</FormLabel>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us how we can help..."
                    rows={4}
                    borderRadius="xl"
                    borderColor="gray.300"
                    focusBorderColor={accentColor}
                  />
                </FormControl>
                <Button
                  type="submit"
                  size="lg"
                  bg={accentColor}
                  color="gray.900"
                  _hover={{ bg: 'yellow.500' }}
                  width="full"
                  borderRadius="xl"
                  fontWeight="bold"
                  boxShadow="lg"
                  isLoading={loading}
                  loadingText="Sending"
                >
                  Send Message
                </Button>
              </VStack>
            </form>
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default Contact;